/**
 * Resolve inbound confirmation → trip city/day placement.
 * Uses HumanReservationDateTime for venue wall-clock parse (#87 / inbound batch).
 */

import type { ItineraryItem, ItineraryItemType, Trip, TripHotelStay } from '../types/client';
import {
  parseHumanReservationDateTime,
  tripStaysFromCityMeta,
  type HumanReservationClues,
} from './humanReservationDateTime';
import { clearHotelConfirmationTripTaskOnEnrich } from './tripTaskTriggers';
import { hmFromUnknown, ymdFromUnknown } from './wallClock';

export type InboundPlacementConflict =
  | 'tripMissing'
  | 'noDate'
  | 'noMatchingCity'
  | 'ambiguousCity';

export interface InboundCityDayOption {
  cityId: string;
  cityName: string;
  dateYmd: string;
  label: string;
}

export interface InboundPlacementPlan {
  tripId: string;
  cityId: string;
  dateYmd: string;
  startHm: string;
  itemType: ItineraryItemType;
  title: string;
  confirmationNumber?: string;
  notes?: string;
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function inferInboundItemType(raw: string | null | undefined): ItineraryItemType {
  const t = String(raw ?? '').toLowerCase();
  if (
    t.includes('hotel') ||
    t.includes('lodging') ||
    t.includes('accommodation') ||
    t.includes('stay')
  ) {
    return 'accommodation';
  }
  if (t.includes('meal') || t.includes('restaurant') || t.includes('dining')) {
    return 'meal';
  }
  if (
    t.includes('flight') ||
    t.includes('car') ||
    t.includes('transport') ||
    t.includes('train') ||
    t.includes('ferry')
  ) {
    return 'transport';
  }
  return 'activity';
}

export function cluesForTrip(
  trip: Trip,
  extras: Omit<HumanReservationClues, 'tripStays'> = {}
): HumanReservationClues {
  return {
    ...extras,
    tripStays: tripStaysFromCityMeta(trip.cityVisitMeta ?? {}, trip.cityNames ?? {}),
  };
}

/** Parse reservation date/time with trip stay clues; returns UTC civil ISO or null. */
export function parseInboundReservationInstant(
  raw: string,
  trip: Trip,
  extras: Omit<HumanReservationClues, 'tripStays'> = {}
): string | null {
  return parseHumanReservationDateTime(raw, cluesForTrip(trip, extras));
}

export function cityDaysOnTrip(trip: Trip): InboundCityDayOption[] {
  const options: InboundCityDayOption[] = [];
  for (const cityId of trip.cityIds ?? []) {
    const meta = trip.cityVisitMeta?.[cityId];
    const start = ymdFromUnknown(meta?.arrivalDate);
    const end = ymdFromUnknown(meta?.departureDate) ?? start;
    if (!start || !end) continue;
    const cityName = trip.cityNames?.[cityId] ?? cityId;
    let cursor = start;
    while (cursor <= end) {
      options.push({
        cityId,
        cityName,
        dateYmd: cursor,
        label: `${cityName} · ${cursor}`,
      });
      const [y, m, d] = cursor.split('-').map(Number);
      const next = new Date(Date.UTC(y, m - 1, d + 1));
      cursor = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`;
    }
  }
  return options;
}

export function matchingCityDaysForYmd(
  trip: Trip,
  dateYmd: string
): InboundCityDayOption[] {
  return cityDaysOnTrip(trip).filter((o) => o.dateYmd === dateYmd);
}

export function resolveInboundPlacement(params: {
  trip: Trip | null | undefined;
  reservationDateRaw: string;
  title: string;
  confirmationType?: string | null;
  confirmationNumber?: string | null;
  notes?: string | null;
  locationText?: string | null;
  emailHints?: string | null;
  selectedCityId?: string | null;
}):
  | { ok: true; plan: InboundPlacementPlan }
  | { ok: false; conflict: InboundPlacementConflict; dateYmd?: string; candidates?: InboundCityDayOption[] } {
  const trip = params.trip;
  if (!trip) return { ok: false, conflict: 'tripMissing' };

  const instant = parseInboundReservationInstant(params.reservationDateRaw, trip, {
    locationText: params.locationText,
    emailHints: params.emailHints,
  });
  if (!instant) return { ok: false, conflict: 'noDate' };

  const dateYmd = ymdFromUnknown(instant);
  const startHm = hmFromUnknown(instant) ?? '12:00';
  if (!dateYmd) return { ok: false, conflict: 'noDate' };

  const matches = matchingCityDaysForYmd(trip, dateYmd);
  let cityId = params.selectedCityId?.trim() || '';
  if (cityId) {
    const still = matches.find((m) => m.cityId === cityId);
    if (!still) {
      // Allow explicit city pick even if date is outside stay window (manual override).
      if (!trip.cityIds?.includes(cityId)) {
        return { ok: false, conflict: 'noMatchingCity', dateYmd, candidates: matches };
      }
    }
  } else if (matches.length === 0) {
    return { ok: false, conflict: 'noMatchingCity', dateYmd, candidates: [] };
  } else if (matches.length > 1) {
    return { ok: false, conflict: 'ambiguousCity', dateYmd, candidates: matches };
  } else {
    cityId = matches[0].cityId;
  }

  return {
    ok: true,
    plan: {
      tripId: trip.id,
      cityId,
      dateYmd,
      startHm,
      itemType: inferInboundItemType(params.confirmationType),
      title: params.title.trim() || 'Confirmation',
      confirmationNumber: params.confirmationNumber?.trim() || undefined,
      notes: params.notes?.trim() || undefined,
    },
  };
}

/** Apply a resolved placement onto trip itinerary (+ hotel stay / task clear when lodging). */
export function applyInboundPlacementToTrip(
  trip: Trip,
  plan: InboundPlacementPlan
): Trip {
  const item: ItineraryItem = {
    id: newId(),
    title: plan.title,
    type: plan.itemType,
    time: plan.startHm,
    confirmationNumber: plan.confirmationNumber,
    notes: plan.notes,
  };

  const days = [...(trip.itineraryDays ?? [])];
  const dayIdx = days.findIndex(
    (d) => d.date === plan.dateYmd && (d.cityId == null || d.cityId === plan.cityId)
  );
  if (dayIdx >= 0) {
    days[dayIdx] = {
      ...days[dayIdx],
      items: [...(days[dayIdx].items ?? []), item],
      cityId: days[dayIdx].cityId ?? plan.cityId,
    };
  } else {
    days.push({ date: plan.dateYmd, cityId: plan.cityId, items: [item] });
  }

  let next: Trip = { ...trip, itineraryDays: days };
  let taskItems = trip.taskItems ?? [];

  if (plan.itemType === 'accommodation' && plan.confirmationNumber) {
    const stays = [...(trip.hotelStaysByCity?.[plan.cityId] ?? [])];
    let stayId: string | null = null;
    const existingIdx = stays.findIndex((s) =>
      s.hotelName.trim().toLowerCase() === plan.title.trim().toLowerCase()
    );
    if (existingIdx >= 0) {
      const prev = stays[existingIdx];
      stayId = prev.id;
      const confirmations = [...(prev.confirmations ?? [])];
      if (!confirmations.includes(plan.confirmationNumber)) {
        confirmations.push(plan.confirmationNumber);
      }
      stays[existingIdx] = { ...prev, confirmations };
    } else {
      stayId = newId();
      const stay: TripHotelStay = {
        id: stayId,
        hotelName: plan.title,
        checkInDate: plan.dateYmd,
        confirmations: [plan.confirmationNumber],
      };
      stays.push(stay);
    }
    next = {
      ...next,
      hotelStaysByCity: {
        ...(trip.hotelStaysByCity ?? {}),
        [plan.cityId]: stays,
      },
    };
    if (stayId) {
      taskItems = clearHotelConfirmationTripTaskOnEnrich({
        taskItems,
        hotelStayId: stayId,
        confirmationNumber: plan.confirmationNumber,
      });
    }
  }

  return { ...next, taskItems };
}
